/**
 * Public types for the Journey Embed SDK.
 *
 * This package is deliberately self-contained: nothing here depends on
 * `@epilot/journey-logic-commons` or any other internal epilot package,
 * so consumers installing from npm never pull in private dependencies.
 */

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum EMBED_MODE {
  INLINE = 'inline',
  FULL_SCREEN = 'full-screen'
}

export enum EMBED_AS {
  IFRAME = 'iframe',
  WEB_COMPONENT = 'web-component'
}

export enum LANGUAGE {
  DE = 'de',
  EN = 'en',
  FR = 'fr'
}

export enum JourneyEventType {
  init = 'EPILOT/INIT',
  updateJourney = 'EPILOT/UPDATE_JOURNEY',
  exitFullScreen = 'EPILOT/EXIT_FULLSCREEN',
  enterFullScreen = 'EPILOT/ENTER_FULLSCREEN',
  closeJourney = 'EPILOT/CLOSE_JOURNEY',
  formChange = 'EPILOT/FORM_EVENT',
  pageView = 'EPILOT/USER_EVENT/PAGE_VIEW',
  journeyIframeReady = 'EPILOT/JOURNEY_LOADED'
}

// ─── Release channels ─────────────────────────────────────────────────────────

export type EmbedChannel = 'stable' | 'canary'

// ─── Data types ───────────────────────────────────────────────────────────────

/**
 * Key/value pairs forwarded to the journey as contextual values.
 *
 * Values are always delivered to the journey app as strings — the SDK
 * coerces numbers to strings on the iframe transport (URL params and
 * the `INIT` postMessage payload) so that behaviour is consistent with
 * the web-component path, which has always coerced numbers via the
 * custom element's attribute parser.
 */
export type ContextData = Record<string, string | number>

/**
 * Controls the display of a single block — currently only disabling fields.
 *
 * The target block can be addressed in one of two ways:
 *
 * - **By stable block ID (recommended)** — set {@link blockId}. Block IDs are
 *   unique journey-wide and unaffected by block renames or step reordering, so
 *   the setting keeps working when builders edit the journey.
 * - **By block name + step index (legacy)** — set {@link blockName} together
 *   with {@link stepIndex}. This form breaks silently when blocks are renamed
 *   or steps reordered.
 *
 * When {@link blockId} is provided it takes priority over the
 * {@link blockName} + {@link stepIndex} pair.
 */
export type BlockDisplaySetting = {
  type: 'DISABLED'
  /**
   * Stable, journey-wide ID of the target block. Unaffected by block renames
   * or step reordering. Takes precedence over {@link blockName} +
   * {@link stepIndex} when provided.
   */
  blockId?: string
  /**
   * Name of the target block.
   *
   * @deprecated Prefer {@link blockId}. Block names change when builders rename
   * blocks, silently breaking the setting; the stable block ID is unaffected.
   */
  blockName?: string
  /**
   * 0-based index of the step the target block lives in.
   *
   * @deprecated Prefer {@link blockId}. Step indices shift when steps are
   * reordered, silently breaking the setting; the stable block ID is unaffected.
   */
  stepIndex?: number
  blockFields?: string[]
}

/**
 * Pre-fill values for a single block, e.g. `{ city: 'Berlin' }`.
 * The exact shape depends on the target block type.
 */
export type BlockInjectionValue = Record<string, unknown>

export type DataInjectionOptions = {
  /**
   * The step to start the Journey from, by 0-based index.
   *
   * @deprecated Prefer {@link DataInjectionOptions.initialStepId}. Step indices
   * shift when steps are reordered, silently breaking the integration; the
   * stable step ID is unaffected by reordering.
   */
  initialStepIndex?: number
  /**
   * The step to start the Journey from, by stable step ID.
   *
   * Unaffected by step reordering. Takes precedence over
   * {@link DataInjectionOptions.initialStepIndex} when both are provided.
   */
  initialStepId?: string
  /**
   * Pre-fill data for blocks. Two shapes are supported:
   *
   * - **By stable block ID (recommended)** — a flat object keyed by block ID.
   *   Block IDs are unique journey-wide and unaffected by block renames or
   *   step reordering, so the integration keeps working when builders edit
   *   the journey:
   *   ```ts
   *   initialState: { 'b1f2…uuid': { city: 'Berlin' } }
   *   ```
   * - **By step index + block name (legacy)** — an array where each entry
   *   corresponds to a step by index, keyed by block name. This form breaks
   *   silently when blocks are renamed or steps reordered:
   *   ```ts
   *   initialState: [{ Adresse: { city: 'Berlin' } }]
   *   ```
   *
   * When the block-ID form is provided it takes priority over the legacy form.
   */
  initialState?: Record<string, BlockInjectionValue> | Record<string, unknown>[]
  /** Controls which blocks/fields are disabled. */
  blocksDisplaySettings?: BlockDisplaySetting[]
}

// ─── Configuration ────────────────────────────────────────────────────────────

export type EmbeddingOptions = {
  /**
   * Base URL of the Journey app. Used to build iframe `src` URLs and to
   * derive the `<epilot-journey>` web component script URL.
   * @default 'https://journey.epilot.io'
   */
  baseUrl?: string
  /**
   * Override the `<epilot-journey>` web component script URL. Takes
   * precedence over `baseUrl + channel` when set. Primarily used for
   * local development where the web component runs on a separate port.
   */
  webComponentScriptUrl?: string
  /** Enables debug logging in iframe-resizer. */
  debug?: boolean
}

/** Callback invoked when the SDK is ready. Receives the SDK instance. */
export type OnReadyCallback = (epilot: import('./sdk').Epilot) => void
